import { Location } from '@angular/common';
import { Component, DoCheck, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { AnalyticEvent } from 'app/lib/analytic-event';
import { ApiService } from 'app/lib/api.service';
import { LogService } from 'app/lib/log.service';
import { SeriesService } from 'app/lib/series.service';
import { UserService } from 'app/lib/user.service';
import { DailySeries, Reads, voice } from 'app/models/course';
import { Organization, OrganizationCourse } from 'app/models/organization';
import { User } from 'app/models/user';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Component({
    selector: 'app-series-category-details',
    templateUrl: './series-category-details.component.html',
    styleUrls: ['./series-category-details.component.scss'],
    standalone: false
})
export class SeriesCategoryDetailsComponent implements OnInit, DoCheck {
    user: User;
    organization: any;
    title = '';
    lang: string = 'en';
    categoryList = [];
    selectedCategoryId: number = 0;
    selectedCategory: any = {};
    videoSeriesList: any = [];
    voiceSeriesList: any = [];
    storiesList: voice[] = [];
    burningQuestionsList: voice[] = [];
    readsList: Reads[] = [];
    seriesTypes: Array<any> = ['video', 'audio'];
    isVideoSeriesloaded: boolean = false;
    isVoiceSeriesloaded: boolean = false;
    isReadsContentListLoaded: boolean = false;
    isDailyContentListLoaded: boolean = false;
    enableVoiceSeries: boolean;
    enableReadsSeries: boolean;
    enableDailySeries: boolean;
    hasVideoContent: boolean = false;
    hasStoryContent: boolean = false;
    hasBqContent: boolean = false;
    hasDailyContent: boolean = false;
    hasReadsContent: boolean = false;
    pastDailyContents: DailySeries[] = [];

    constructor(
        private userService: UserService,
        private api: ApiService,
        private logService: LogService,
        private translate: TranslateService,
        private route: ActivatedRoute,
        private seriesService: SeriesService,
        private log: LogService,
        private router: Router,
        private location: Location
    ) {
        this.selectedCategoryId =
            +this.route.snapshot.paramMap.get('category') || 0;
        this.enableVoiceSeries =
            this.seriesService.checkSeriesTypesEnabled().enableStorySeries ||
            this.seriesService.checkSeriesTypesEnabled()
                .enableBurningQuestionSeries;
        this.isVoiceSeriesloaded =
            !this.seriesService.checkSeriesTypesEnabled().enableStorySeries &&
            !this.seriesService.checkSeriesTypesEnabled()
                .enableBurningQuestionSeries;
        this.enableDailySeries =
            this.seriesService.checkSeriesTypesEnabled().enableDailySeries;
        this.enableReadsSeries =
            this.seriesService.checkSeriesTypesEnabled().enableReadsSeries;
        this.getCategories();
        this.getVideoSeries();
        this.enableVoiceSeries && this.getVoiceSeries();
        this.enableDailySeries && this.getDailyContentList();
        this.enableReadsSeries && this.getReadsContentList();
    }

    ngOnInit(): void {
        this.user = this.userService.getUser();
        this.lang = this.translate.currentLang;
        this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
            // this.loadCourses();
        });
    }
    ngDoCheck() {
        this.lang = this.translate.currentLang;
    }
    getCategories() {
        this.seriesService.categories.subscribe(data => {
            this.categoryList = data;
            if (this.categoryList.length) {
                this.selectedCategory = this.categoryList.find(
                    item => item.ID == this.selectedCategoryId,
                );
                this.hasVideoContent = this.selectedCategory.hasVideos > 0;
                this.hasStoryContent = this.selectedCategory.hasStories > 0;
                this.hasBqContent =
                    this.selectedCategory.hasBurningQuestions > 0;
                this.hasDailyContent = this.selectedCategory.hasDailySeries > 0;
                this.hasReadsContent = this.selectedCategory.hasReads > 0;
                this.log.event(
                    AnalyticEvent.internalPagesEvents.seriesCategoryDetailsPage,
                    '',
                    {
                        [AnalyticEvent.commonEvents.category]:
                            this.selectedCategory.Title,
                    }
                );
                this.title = Object.keys(this.selectedCategory)
                    ? this.translate.instant(
                          'series.categories.' + this.selectedCategory.Key
                      )
                    : '';
            }
        });
        this.seriesService.getCategories();
    }

    getVideoSeries() {
        this.isVideoSeriesloaded = false;
        this.seriesService.videoSeries.subscribe((data: any) => {
            if (Object.keys(data).length > 0) this.isVideoSeriesloaded = true;
            if (!data || data.status == 'error') return;
            if (data.status == 'success') {
                this.filterVideoSeriesByCategory(data.seriesList);
            }
        });
        this.seriesService.getCourses(false, false);
    }

    getVoiceSeries() {
        this.isVoiceSeriesloaded = false;
        let apiCalls: { [key: string]: any } = {};
        if (this.seriesService.checkSeriesTypesEnabled().enableStorySeries) {
            apiCalls.storyData = this.api.get('series/stories').pipe(
                catchError(() => of([])) // Return empty array on error
            );
        }
        if (
            this.seriesService.checkSeriesTypesEnabled()
                .enableBurningQuestionSeries
        ) {
            apiCalls.burningQuestionData = this.api
                .get('series/burningquestions')
                .pipe(catchError(() => of([]))); // Return empty array on error
        }
        forkJoin(apiCalls).subscribe((response: any) => {
            this.isVoiceSeriesloaded = true;
            const storyData = this.user?.primaryOrganization
                ? this.getEnabledVoiceSeries(
                      response.storyData || [],
                      this.user?.primaryOrganization.storiesConfig
                  )
                : response.storyData?.filter((def: any) => def?.Active) ?? [];
            const burningQuestionData = this.user?.primaryOrganization
                ? this.getEnabledVoiceSeries(
                      response.burningQuestionData || [],
                      this.user?.primaryOrganization.burningQuestionsConfig
                  )
                : response.burningQuestionData?.filter(
                      (def: any) => def?.Active
                  ) ?? [];

            this.storiesList = voice.initializeArray(storyData, 'stories');
            this.burningQuestionsList = voice.initializeArray(
                burningQuestionData,
                'burningQuestion'
            );
            const combinedVoiceSeriesList = [
                ...this.storiesList,
                ...this.burningQuestionsList,
            ];
            this.filterVoiceSeriesByCategory(combinedVoiceSeriesList);
        });
    }

    getEnabledVoiceSeries(seriesList: any[], config: any[]): any[] {
        const primaryOrganizationSeriesMap = config?.reduce(
            (acc, item: any) => {
                acc[item.seriesId] = item;
                return acc;
            },
            {}
        );
        const newArray: any[] =
            seriesList?.filter(
                (def: any) =>
                    primaryOrganizationSeriesMap[def.ID]?.active &&
                    primaryOrganizationSeriesMap[def.ID]?.enabled
            ) ?? [];
        return newArray;
    }

    getReadsContentList() {
        this.isReadsContentListLoaded = false;
        this.api.get("series/reads").subscribe(
        (response: any) => {
            //   this.log.event(
            //     AnalyticEvents.internalPagesEvents.pastDailyContentLoaded
            //   );
            this.isReadsContentListLoaded = true;
            const readsData = this.user?.primaryOrganization
            ? this.getEnabledReadsSeries(
                response || [],
                this.user?.primaryOrganization.readsConfig,
                )
            : response?.filter((def: any) => def?.Active) ?? [];
            this.filterReadableSeriesByCategory(
            Reads.initializeArray(readsData),
            );
        },

        (error: any) => {
            //   this.log.error("Error getting daily content list. " + error.message);
            //   this.log.event(
            //     AnalyticEvents.internalPagesEvents.pastDailyContentFetchError
            //   );
        },
        () => {},
        );
    }

    getEnabledReadsSeries(seriesList: any[], config: any[]): any[] {
        const primaryOrganizationSeriesMap = config?.reduce((acc, item: any) => {
        acc[item.seriesId] = item;
        return acc;
        }, {});
        const newArray: any[] =
        seriesList?.filter(
            (def: any) =>
            primaryOrganizationSeriesMap[def.ID]?.active &&
            primaryOrganizationSeriesMap[def.ID]?.enabled,
        ) ?? [];
        return newArray;
    }

    filterVoiceSeriesByCategory(seriesList) {
        if (Object.keys(seriesList).length) {
            this.voiceSeriesList = Object.values(seriesList).filter(
                (value: any) => value.category == this.selectedCategoryId
            );
        } else {
            this.voiceSeriesList = [];
        }
        this.isVoiceSeriesloaded = true;
    }

    filterVideoSeriesByCategory(courses) {
        let courseList = this.seriesService.setVideoSeriesPermissions(courses);
        if (Object.keys(courseList).length) {
            this.videoSeriesList = Object.values(courseList).filter(
                (value: any) => value.category == this.selectedCategoryId
            );
        } else {
            this.videoSeriesList = [];
        }
        this.isVideoSeriesloaded = true;
    }

    filterReadableSeriesByCategory(seriesList: Reads[]) {
        if (Object.keys(seriesList).length) {
        this.readsList = Object.values(seriesList).filter(
            (value: any) => value.category == this.selectedCategoryId,
        );
        } else {
        this.readsList = [];
        }
    }
    goToCourse(course) {
        this.router.navigateByUrl('app/series-details/' + course.key);
    }
    goToVoiceStory(type, story) {
        this.router.navigate(['/app/series/voices/' + type + '/' + story.id]);
    }
    goBack() {
        this.location.back();
    }
    getName(item) {
        return 'jhbd';
    }
    selectedOption: string = '';
    getDailyContentList() {
        this.isDailyContentListLoaded = false;
        this.api
            .get('series/dailyseries/previous', {
                category: this.selectedCategoryId,
            })
            .subscribe(
                (result: any) => {
                    this.log.event(
                        AnalyticEvent.internalPagesEvents.pastDailyContentLoaded
                    );
                    this.isDailyContentListLoaded = true;
                    if (result?.length) {
                        this.pastDailyContents =
                            DailySeries.initializeArray(result);
                    }
                },
                (error: any) => {
                    this.log.error(
                        'Error getting daily content list. ' + error.message
                    );
                    this.log.event(
                        AnalyticEvent.internalPagesEvents
                            .pastDailyContentFetchError
                    );
                },
                () => {}
            );
    }

    goToRead(reads: any) {
          window.open(reads.fileUrl, '_blank');
          this.log.event(AnalyticEvent.internalPagesEvents.customOrgReadItemClicked, '',
            { title: reads["title" + this.lang] },
          );
          reads?.id && this.seriesService.logReadsSeriesClickCount(reads.id);
    }
}
